import { useSyncExternalStore } from 'react';

/**
 * Tutorial guiado pelo próprio Secretário: ele olha para o botão, o botão ganha
 * um anel, ele diz uma frase e a pessoa toca. Dois formatos (estudo de 05/10):
 * o passeio de 4 passos do primeiro acesso (dá para pular) e as missões da
 * cartela ("Me mostra como"), um passo de cada vez, sempre com a ação de verdade.
 * Nada de máscara escura nem de tour longo: 3 a 4 passos terminam; 7 não.
 */
export type TutorialStep = {
  /** Seletor do alvo na tela. */
  target: string;
  /** O que ele diz (vai para a boca e para o balão). */
  say: string;
  /** O toque no alvo é só para aprender: não dispara a ação de verdade. */
  intercept?: boolean;
  /** O toque abre um painel: o passo termina quando o painel fecha. */
  waitClose?: boolean;
};

export type TutorialState = {
  kind: 'tour' | 'mission';
  steps: TutorialStep[];
  index: number;
  /** Passo cumprido: fase final antes de o próximo começar (ou de terminar). */
  finished: boolean;
};

const DONE_KEY = 'secretario.passeio';

export const TOUR: TutorialStep[] = [
  {
    target: '.stg-talk',
    say: 'Esse é o Falar. Toque e me peça qualquer coisa, como numa conversa.',
    intercept: true,
  },
  {
    target: '.rail-agenda_day',
    say: 'Seus atalhos: Agenda, Contas, Pessoas e Documentos. Toque em Agenda para ver.',
    waitClose: true,
  },
  {
    target: '.stg-handle',
    say: 'O Hoje mostra o que espera você e o que eu já fiz. Toque para abrir.',
    waitClose: true,
  },
  {
    target: '.psn-me',
    say: 'No seu avatar ficam o meu jeito, os Ajustes e o seu plano.',
    intercept: true,
  },
];

export const TOUR_END = 'Pronto. Pela voz você faz tudo isso sem tocar em nada.';

/** Missão de cada passo da cartela: um alvo e a ação de verdade. */
const MISSIONS: Record<string, TutorialStep[]> = {
  pedido: [
    {
      target: '.stg-talk',
      say: 'Toque no Falar e diga: me lembra da luz dia 10 às 9 da manhã.',
    },
  ],
  voz: [
    {
      target: '.stg-talk',
      say: 'Segure o Falar e conversamos ao vivo. Solte quando terminar.',
    },
  ],
  conta: [
    {
      target: '.stg-talk',
      say: 'Toque no Falar e diga: coloca na lista de contas a luz, 120 reais, dia 10.',
    },
  ],
  pessoas: [
    {
      target: '.rail-people',
      say: 'Toque em Pessoas. Lá dentro, em Contatos, dá para trazer a sua agenda.',
    },
  ],
  documento: [
    {
      target: '.rail-deliveries',
      say: 'Em Documentos eu guardo o que preparo. Ou só me peça na conversa: um roteiro, uma carta.',
      waitClose: true,
    },
  ],
  caderno: [
    {
      target: '.psn-me',
      say: 'O Caderno fica no menu do seu avatar: toque e veja o que eu já sei de você.',
    },
  ],
  jeito: [
    {
      target: '.psn-me',
      say: 'Mudar o jeito também fica no menu do seu avatar: personagem, voz e nome.',
    },
  ],
};

export function missionFor(journeyStepId: string): TutorialStep[] | null {
  return MISSIONS[journeyStepId] ?? null;
}

let state: TutorialState | null = null;
const listeners = new Set<() => void>();

function set(next: TutorialState | null): void {
  state = next;
  // Só em desenvolvimento: as provas de tela leem o passo atual por aqui.
  if (import.meta.env.DEV) (window as unknown as { __tutorial?: unknown }).__tutorial = next;
  listeners.forEach((listener) => listener());
}

/**
 * Com o Hoje fixo (padrão), ele já está aberto: o passo do Hoje aponta para a folha e pede
 * para puxar, e os atalhos são os da faixa dentro dele.
 */
const DOCKED_TOUR: TutorialStep[] = TOUR.map((step) =>
  step.target === '.stg-handle'
    ? {
        target: '.hj-dock-hd',
        say: 'Aqui embaixo fica o Hoje: o que espera você e o que eu já fiz. Puxe para cima para ver tudo.',
        intercept: true,
      }
    : step.target === '.rail-agenda_day'
      ? { ...step, target: '.hj-at.rail-agenda_day' }
      : step,
);

export function startTour(): void {
  const docked = typeof document !== 'undefined' && document.querySelector('.hj-dock') !== null;
  set({ kind: 'tour', steps: docked ? DOCKED_TOUR : TOUR, index: 0, finished: false });
}

export function startMission(journeyStepId: string): boolean {
  const steps = missionFor(journeyStepId);
  if (!steps) return false;
  set({ kind: 'mission', steps, index: 0, finished: false });
  return true;
}

/** O passo atual foi cumprido: segue para o próximo ou termina. */
export function advanceTutorial(): void {
  if (!state) return;
  const index = state.index + 1;
  if (index >= state.steps.length) {
    if (state.kind === 'tour') rememberTour('feito');
    set({ ...state, finished: true });
    return;
  }
  set({ ...state, index, finished: false });
}

export function endTutorial(skipped = false): void {
  if (state?.kind === 'tour' && skipped) rememberTour('pulado');
  set(null);
}

export function tutorialState(): TutorialState | null {
  return state;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useTutorial(): TutorialState | null {
  return useSyncExternalStore(subscribe, tutorialState, tutorialState);
}

/** O que ele diz agora, se o tutorial estiver falando. */
export function tutorialSay(current: TutorialState | null): string | null {
  if (!current) return null;
  if (current.finished) return current.kind === 'tour' ? TOUR_END : 'Isso. Agora é com você.';
  return current.steps[current.index]?.say ?? null;
}

export function tourDone(): boolean {
  try {
    const value = window.localStorage.getItem(DONE_KEY);
    return value === 'feito' || value === 'pulado';
  } catch {
    return false;
  }
}

export function rememberTour(value: 'feito' | 'pulado'): void {
  try {
    window.localStorage.setItem(DONE_KEY, value);
  } catch {
    /* sem armazenamento: o passeio pode ser oferecido de novo */
  }
}

/** Só para os testes. */
export function resetTutorial(): void {
  state = null;
}
